import {
  Grid,
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Stack,
  Typography,
  SelectChangeEvent,
  useTheme,
  Box,
  useMediaQuery
} from '@mui/material';
import React from 'react';
import { z as zod } from 'zod';
import { ArmedIcon } from '../../../../assets/icons/icons';
import { AgencyOfficialRolesMap } from '../../';
import { FormOfficialData, OfficialFormHelpers } from '../../../../utils/officialFormHelpers';
import { useUser } from '@hooks/useUser';
import { useInfo } from '@hooks/useInfo';
import { isRoleAssignable } from '../../../../utils/authorization';

type SurePassTextFieldProps = {
  name: string;
  label: string;
  placeholder: string;
  value: string;
  required: boolean;
  isSmallScreen: boolean;
  autoFocus?: boolean;
  fieldErrors: { [fieldName: string]: string[] | undefined; };
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
};
const SurePassTextField = (props: SurePassTextFieldProps) => {
  const labelSuffix = props.required ? '(Required)' : '(Optional)';
  const label = `${props.label} ${labelSuffix}`;
  return (
    <Grid size={props.isSmallScreen ? 12 : 6}>
      <TextField
        autoFocus={props.autoFocus}
        error={!!props.fieldErrors[props.name]}
        label={label}
        name={props.name}
        onChange={props.onChange}
        placeholder={props.placeholder}
        slotProps={{
          inputLabel: {
            shrink: true
          }
        }}
        value={props.value}
        fullWidth
      />
    </Grid>
  );
};

interface AddAgencyOfficialDialogContentProps {
  handleChange: {
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleSelectChange: (e: SelectChangeEvent) => void;
    handleSwitchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  };
  formOfficialData: FormOfficialData;
  roles: AgencyOfficialRolesMap;
  formErrors: zod.ZodError<any> | null;
}

const AddAgencyOfficialDialogContent = ({
  handleChange,
  formOfficialData,
  roles,
  formErrors
}: AddAgencyOfficialDialogContentProps) => {
  const theme = useTheme();
  const padding = theme.spacing(3);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const fieldErrors = formErrors?.formErrors.fieldErrors || {};
  const roleError = !!fieldErrors['role'];
  const isSecurityContractor = OfficialFormHelpers.isSecurityContractor(formOfficialData);
  const isEmailRequired = !isSecurityContractor;
  const { user } = useUser();
  const { employmentTypeOptions } = useInfo();
  return (
    <DialogContent sx={{ padding }}>
      <Grid
        container
        spacing={3}
        sx={{
          padding: 1,
          backgroundColor: theme.palette.grey[50],
          borderTopLeftRadius: Number(theme.shape.borderRadius) * 3,
          borderTopRightRadius: Number(theme.shape.borderRadius) * 3
        }}>
        <Grid
          container
          spacing={3}
          sx={{
            padding: 1,
            flexGrow: 1
          }}>

          <SurePassTextField
            name="first_name"
            label="First Name"
            placeholder="First Name"
            value={formOfficialData.first_name}
            required={true}
            onChange={handleChange.handleInputChange}
            isSmallScreen={isSmallScreen}
            fieldErrors={fieldErrors}
            autoFocus={true}
          />

          <SurePassTextField
            name="last_name"
            label="Last Name"
            placeholder="Last Name"
            value={formOfficialData.last_name}
            required={true}
            onChange={handleChange.handleInputChange}
            isSmallScreen={isSmallScreen}
            fieldErrors={fieldErrors}
          />

          <Grid size={isSmallScreen ? 12 : 6} key="role-select-control">
            <FormControl fullWidth variant="outlined" required>
              <InputLabel
                id="role-select-label"
                className={roleError ? 'Mui-error' : ''}
                required={false}
              >
                Role (Required)
              </InputLabel>
              <Select
                labelId="role-select-label"
                id="role-select"
                data-testid="role-select"
                name="role"
                value={roles.size > 0 ? formOfficialData.role : ''}
                onChange={handleChange.handleSelectChange}
                displayEmpty
                renderValue={(selected) =>
                  selected || (roles.size > 0 ? formOfficialData.role : '')
                }
                label="Role (Required)"
                required={false}
                error={roleError}
              >
                {Array.from(roles)
                  .filter(([key]) => isRoleAssignable(key, user?.profile))
                  .map(([key, value]) => (
                    <MenuItem key={value} value={key} selected={formOfficialData.role === key}>
                      {key}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          {formOfficialData.role !== '' &&
            <>
              <SurePassTextField
                name="email"
                label="Email Address"
                placeholder="email@gmail.com"
                value={formOfficialData.email}
                required={isEmailRequired}
                onChange={handleChange.handleInputChange}
                isSmallScreen={isSmallScreen}
                fieldErrors={fieldErrors}
              />

              {!isSecurityContractor &&
                <>
                  <SurePassTextField
                    name="title"
                    label="Title"
                    placeholder="Overnight Facility Security Agent"
                    value={formOfficialData.title}
                    required={false}
                    onChange={handleChange.handleInputChange}
                    isSmallScreen={isSmallScreen}
                    fieldErrors={fieldErrors}
                  />

                  <SurePassTextField
                    name="badge_number"
                    label="Badge"
                    placeholder="1528"
                    value={formOfficialData.badge_number}
                    required={false}
                    onChange={handleChange.handleInputChange}
                    isSmallScreen={isSmallScreen}
                    fieldErrors={fieldErrors}
                  />

                  <SurePassTextField
                    name="rank"
                    label="Rank"
                    placeholder="Sergeant"
                    value={formOfficialData.rank}
                    required={false}
                    onChange={handleChange.handleInputChange}
                    isSmallScreen={isSmallScreen}
                    fieldErrors={fieldErrors}
                  />

                  <SurePassTextField
                    name="phone_number"
                    label="Phone"
                    placeholder="555-123-4567"
                    value={formOfficialData.phone_number}
                    required={false}
                    onChange={handleChange.handleInputChange}
                    isSmallScreen={isSmallScreen}
                    fieldErrors={fieldErrors}
                  />

                  <Grid size={isSmallScreen ? 12 : 6}>
                    <FormControl fullWidth variant="outlined" required>
                      <InputLabel
                        id="employment_type-select-label"
                        required={false}
                      >
                        Designation (Required)
                      </InputLabel>
                      <Select
                        id="employment_type-select"
                        labelId="employment_type-select-label"
                        name="employment_type"
                        value={formOfficialData.employment_type}
                        onChange={handleChange.handleSelectChange}
                        label="Designation (Required)"
                      >
                        {employmentTypeOptions.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              }

            </>
          }

        </Grid>
      </Grid>
      <Grid
        container
        sx={{
          mt: 1,
          backgroundColor: theme.palette.grey[50],
          borderRadius: theme.shape.borderRadius
        }}>
        <Grid
          size={isSmallScreen && 12}
          sx={{
            padding: padding,
            paddingTop: '21px',
            paddingBottom: '21px',
            backgroundColor: theme.palette.blueGrey[100],
            borderRadius: theme.shape.borderRadius,
            display: isSmallScreen ? 'flex' : 'inerit',
            justifyContent: isSmallScreen ? 'center' : 'inerit'
          }}>
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            <Typography>No</Typography>
            <Switch
              name="armed_permission"
              data-testid="armed_permission"
              checked={formOfficialData.armed_permission}
              onChange={handleChange.handleSwitchChange}
              // A switch only toggles on Space natively; let Enter toggle it too.
              // The keydown bubbles from the focused input to this root handler;
              // clicking that input flips it and fires onChange (handleSwitchChange).
              // preventDefault stops Enter from submitting the surrounding form.
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  (e.target as HTMLInputElement).click();
                }
              }}
              slotProps={{
                input: { 'aria-label': 'controlled' }
              }}
            />
            <Typography>Yes</Typography>
          </Stack>
        </Grid>
        <Grid
          container
          sx={{
            padding: padding,
            alignItems: "center"
          }}>
          <Box
            sx={{
              fontWeight: 600,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <ArmedIcon />
            <Typography sx={{ ml: 1 }}>Permission to be armed</Typography>
          </Box>
        </Grid>
      </Grid>
    </DialogContent>
  );
};

export default AddAgencyOfficialDialogContent;
